import { useState, useEffect, useCallback } from 'react';
import { offlineQueue, QueuedAction } from '@/lib/offlineQueue';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';

export function useOfflineQueue() {
  const [queue, setQueue] = useState<QueuedAction[]>([]);
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();

  const refreshQueue = useCallback(async () => {
    const actions = await offlineQueue.getAll();
    setQueue(actions);
  }, []);

  useEffect(() => {
    refreshQueue();
    
    // Refresh queue every 5 seconds
    const interval = setInterval(refreshQueue, 5000);
    return () => clearInterval(interval);
  }, [refreshQueue]);

  const processAction = async (action: QueuedAction): Promise<boolean> => {
    try {
      await offlineQueue.markAsProcessing(action.id);

      switch (action.entity) {
        case 'opportunity':
          await processOpportunityAction(action);
          break;
        case 'contact':
          await processContactAction(action);
          break;
        case 'company':
          await processCompanyAction(action);
          break;
        case 'email':
          await processEmailAction(action);
          break;
        case 'task':
          await processTaskAction(action);
          break;
        case 'lead':
          await processLeadAction(action);
          break;
      }

      await offlineQueue.remove(action.id);
      return true;
    } catch (error: any) {
      console.error('Failed to process action:', error);
      await offlineQueue.incrementRetry(action.id);
      await offlineQueue.markAsFailed(action.id, error.message);
      return false;
    }
  };

  const processOpportunityAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'create':
        await supabase.from('opportunities').insert(data);
        break;
      case 'update':
        await supabase.from('opportunities').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('opportunities').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['opportunities'], exact: false });
  };

  const processContactAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'create':
        await supabase.from('contacts').insert(data);
        break;
      case 'update':
        await supabase.from('contacts').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('contacts').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['contacts'] });
  };

  const processCompanyAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'create':
        await supabase.from('companies').insert(data);
        break;
      case 'update':
        await supabase.from('companies').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('companies').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  const processEmailAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'update':
        await supabase.from('email_messages').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('email_messages').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['emails'] });
  };

  const processTaskAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'create':
        await supabase.from('tasks').insert(data);
        break;
      case 'update':
        await supabase.from('tasks').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('tasks').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['tasks'] });
  };

  const processLeadAction = async (action: QueuedAction) => {
    const { type, data } = action;
    
    switch (type) {
      case 'create':
        await supabase.from('leads').insert(data);
        break;
      case 'update':
        await supabase.from('leads').update(data).eq('id', data.id);
        break;
      case 'delete':
        await supabase.from('leads').delete().eq('id', data.id);
        break;
    }
    
    queryClient.invalidateQueries({ queryKey: ['leads'] });
  };

  const syncQueue = async () => {
    if (isSyncing) return;
    
    setIsSyncing(true);
    const pendingActions = await offlineQueue.getPending();
    
    if (pendingActions.length === 0) {
      setIsSyncing(false);
      return;
    }

    let successCount = 0;
    let failCount = 0;

    for (const action of pendingActions) {
      const success = await processAction(action);
      if (success) {
        successCount++;
      } else {
        failCount++;
      }
    }

    await refreshQueue();
    setIsSyncing(false);

    if (successCount > 0) {
      toast.success(`Synced ${successCount} action${successCount > 1 ? 's' : ''}`);
    }
    
    if (failCount > 0) {
      toast.error(`Failed to sync ${failCount} action${failCount > 1 ? 's' : ''}`);
    }
  };

  const clearQueue = async () => {
    await offlineQueue.clear();
    await refreshQueue();
    toast.success('Queue cleared');
  };

  const retryFailed = async () => {
    const failedActions = queue.filter(a => a.status === 'failed');
    
    for (const action of failedActions) {
      await offlineQueue.update(action.id, { status: 'pending', retryCount: 0, error: undefined });
    }
    
    await refreshQueue();
    await syncQueue();
  };

  const clearFailed = async () => {
    const failedActions = queue.filter(a => a.status === 'failed');
    
    for (const action of failedActions) {
      await offlineQueue.remove(action.id);
    }
    
    await refreshQueue();
    toast.success(`Cleared ${failedActions.length} failed action${failedActions.length !== 1 ? 's' : ''}`);
  };

  const queueStatus = {
    pending: queue.filter(a => a.status === 'pending').length,
    processing: queue.filter(a => a.status === 'processing').length,
    failed: queue.filter(a => a.status === 'failed').length,
    total: queue.length
  };

  return {
    queue,
    queueStatus,
    pendingCount: queueStatus.pending,
    failedCount: queueStatus.failed,
    isSyncing,
    syncQueue,
    clearQueue,
    clearFailed,
    retryFailed,
    refreshQueue,
  };
}
